import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Sparkles, Wand2, Download, ShoppingCart, RefreshCw } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const styles = [
  { id: "minimal", name: "Minimal", description: "Clean lines, simple shapes" },
  { id: "vintage", name: "Vintage", description: "Retro 80s/90s aesthetic" },
  { id: "streetwear", name: "Streetwear", description: "Urban, bold graphics" },
  { id: "abstract", name: "Abstract", description: "Geometric, modern art" },
  { id: "nature", name: "Nature", description: "Botanical, organic" },
  { id: "pop", name: "Pop Art", description: "Vibrant, comic style" },
];

const promptSuggestions = [
  "A minimalist mountain landscape with geometric shapes",
  "Retro synthwave sunset with palm trees",
  "Abstract skull with colorful flowers",
  "Japanese wave with modern geometric overlay",
  "Vintage motorcycle with worn texture",
  "Space astronaut floating among planets",
];

export default function Generator() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("minimal");
  const [generating, setGenerating] = useState(false);
  const [generatedDesign, setGeneratedDesign] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a description for your design");
      return;
    }

    setGenerating(true);
    setGeneratedDesign(null);

    try {
      const response = await axios.post(`${API}/generate`, {
        prompt: prompt.trim(),
        style: style,
      });

      setGeneratedDesign({
        id: response.data.design_id,
        image_url: response.data.image_url,
        message: response.data.message,
      });
      toast.success("Design generated successfully!");
    } catch (e) {
      const errorMessage = e.response?.data?.detail || "Failed to generate design";
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleAddToCart = async () => {
    if (!generatedDesign) return;

    setAddingToCart(true);
    try {
      await axios.post(`${API}/cart`, { design_id: generatedDesign.id });
      toast.success("Added to cart!");
      navigate("/cart");
    } catch (e) {
      if (e.response?.data?.detail === "Already in cart") {
        toast.info("Already in cart");
      } else {
        toast.error("Failed to add to cart");
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const handleSuggestion = (suggestion) => {
    setPrompt(suggestion);
  };

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="generator-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              AI-Powered Design
            </div>
            <h1 
              className="text-4xl sm:text-5xl font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Create Your Design
            </h1>
            <p className="mt-4 text-lg text-zinc-600 max-w-2xl mx-auto">
              Describe your vision and let AI bring it to life. Generate unique, 
              print-ready t-shirt designs in seconds.
            </p>
          </div>

          {/* Generator Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Controls - Left Panel */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-zinc-200">
                <h3 className="font-medium mb-4">Describe Your Design</h3>
                <Textarea
                  placeholder="Describe your dream t-shirt design..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[120px] resize-none border-zinc-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  data-testid="prompt-input"
                />

                <div className="mt-4">
                  <p className="text-xs text-zinc-500 mb-2">Try a suggestion:</p>
                  <div className="flex flex-wrap gap-2">
                    {promptSuggestions.slice(0, 3).map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestion(suggestion)}
                        className="text-xs bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-full transition-colors text-left"
                      >
                        {suggestion.slice(0, 30)}...
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-zinc-200">
                <h3 className="font-medium mb-4">Style</h3>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="w-full" data-testid="style-select">
                    <SelectValue placeholder="Select a style" />
                  </SelectTrigger>
                  <SelectContent>
                    {styles.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <div>
                          <span className="font-medium">{s.name}</span>
                          <span className="text-zinc-500 ml-2 text-sm">{s.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="w-full btn-accent flex items-center justify-center gap-2 ai-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-5 h-5 spinner" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generate Design
                  </>
                )}
              </button>
            </div>

            {/* Canvas - Right Panel */}
            <div className="lg:col-span-8">
              <div className="bg-white p-6 rounded-2xl border border-zinc-200 min-h-[500px] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Preview</h3>
                  {generatedDesign && (
                    <span className="text-sm text-zinc-500 price-tag">$9.99</span>
                  )}
                </div>

                <div className="flex-1 generator-canvas rounded-xl flex items-center justify-center overflow-hidden">
                  {generating ? (
                    <div className="text-center">
                      <div className="w-16 h-16 border-4 border-zinc-200 border-t-indigo-500 rounded-full spinner mx-auto mb-4"></div>
                      <p className="text-zinc-600">Creating your design...</p>
                      <p className="text-sm text-zinc-400 mt-1">This may take a moment</p>
                    </div>
                  ) : generatedDesign ? (
                    <img
                      src={generatedDesign.image_url}
                      alt="Generated design"
                      className="max-w-full max-h-full object-contain"
                      data-testid="generated-image"
                    />
                  ) : (
                    <div className="text-center text-zinc-400">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Your design will appear here</p>
                      <p className="text-sm mt-1">Enter a prompt and click Generate</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {generatedDesign && !generating && (
                  <div className="flex flex-col sm:flex-row gap-4 mt-6">
                    <button
                      onClick={handleAddToCart}
                      disabled={addingToCart}
                      className="btn-primary flex items-center justify-center gap-2 flex-1"
                      data-testid="add-generated-to-cart"
                    >
                      {addingToCart ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      ) : (
                        <>
                          <ShoppingCart className="w-5 h-5" />
                          Add to Cart - $9.99
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="btn-secondary flex items-center justify-center gap-2"
                      data-testid="regenerate-btn"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Regenerate
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
